import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import logoIconPng from '../logo-icon.svg';
import api from '../../services/api';

function safeParse(key, fallback) { try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch { return fallback; } }
function getFirstName(name) { return String(name || 'Usuário').trim().split(' ')[0] || 'Usuário'; }
function getInitials(name) { const parts = String(name || 'Lumix').trim().split(' ').filter(Boolean); if (!parts.length) return 'LX'; const first = parts[0]?.[0] || 'L'; const second = parts[1]?.[0] || parts[0]?.[1] || 'X'; return `${first}${second}`.toUpperCase(); }
function getTodayStr() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function parseLocalDate(str) { const [y, m, d] = String(str || getTodayStr()).split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1); }
function formatDateBR(str, options = { day: '2-digit', month: '2-digit', year: 'numeric' }) { return new Intl.DateTimeFormat('pt-BR', options).format(parseLocalDate(str)); }
function normalizePhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.startsWith('55') ? digits : `55${digits}`;
}
function applyTemplate(template, cliente, tipoLabel) {
  return String(template || '').replaceAll('{nome}', cliente.nome || 'cliente').replaceAll('{data}', formatDateBR(cliente.data)).replaceAll('{hora}', cliente.hora || '').replaceAll('{tipo}', tipoLabel || 'compromisso').replaceAll('{loja}', cliente.loja || 'Lumix Óticas');
}

function storageScope(user = {}) {
  const empresa = user.empresaId?._id || user.empresaId || user.empresa?._id || user.empresa?.id || 'empresa';
  const unidade = user.unidadeId?._id || user.unidadeId || user.lojaId || user.loja || 'matriz';
  return `${String(empresa)}_${String(unidade)}`.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function objectId(value) { if (!value) return ''; if (typeof value === 'object') return String(value._id || value.id || value.empresaId || value.canalWhatsappId || ''); return String(value); }
function getEmpresaIdFromUser(user = {}) { return objectId(user.empresaId || user.empresa?._id || user.empresa?.id || user.empresaMatrizId || user.matrizId || ''); }
function getEmpresaIdFromCanal(canal) {
  if (!canal || typeof canal !== 'object') return '';
  return objectId(canal.empresaId || canal.empresa?._id || canal.empresa?.id || '');
}
function getCanalId(canal) {
  if (!canal || typeof canal !== 'object') return '';
  return objectId(canal._id || canal.id || canal.canalWhatsappId || '');
}
function extractEmpresa(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.empresa && typeof payload.empresa === 'object') return payload.empresa;
  if (payload.minhaEmpresa && typeof payload.minhaEmpresa === 'object') return payload.minhaEmpresa;
  if (payload.data?.empresa && typeof payload.data.empresa === 'object') return payload.data.empresa;
  if (payload.data && !Array.isArray(payload.data) && typeof payload.data === 'object') return payload.data;
  if (payload._id || payload.id) return payload;
  return null;
}
function extractCanais(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.canais)) return payload.canais;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.canais)) return payload.data.canais;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function extractAgendamentos(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.agendamentos)) return payload.agendamentos;
  if (Array.isArray(payload?.data?.agendamentos)) return payload.data.agendamentos;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeAgendamento(item = {}) {
  return {
    ...item,
    id: objectId(item.id || item._id),
    data: String(item.data || ''),
    hora: String(item.hora || ''),
    nome: String(item.nome || ''),
    telefone: String(item.telefone || ''),
    movimentacoes: Array.isArray(item.movimentacoes) ? item.movimentacoes : [],
    avisosWhatsapp: Array.isArray(item.avisosWhatsapp) ? item.avisosWhatsapp : []
  };
}

function extractTemplates(payload) {
  const value = payload?.templates || payload?.data?.templates || {};
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
function empresaLabel(empresa, user = {}) { return String(empresa?.nomeFantasia || empresa?.nome || empresa?.razaoSocial || user.loja || 'Empresa'); }
function canalLabel(canal = {}) { const nome = canal.nome || canal.apelido || canal.descricao || 'WhatsApp'; const numero = canal.numero ? String(canal.numero) : ''; return numero ? `${nome} · ${numero}` : nome; }
async function apiRequest(url, options = {}) {
  const method = String(options.method || 'GET').toLowerCase();

  let data = options.data;
  if (data === undefined && options.body !== undefined) {
    if (typeof options.body === 'string') {
      try {
        data = JSON.parse(options.body);
      } catch {
        data = options.body;
      }
    } else {
      data = options.body;
    }
  }

  try {
    const response = await api.request({
      url,
      method,
      data,
      params: options.params,
      headers: options.headers
    });

    const payload = response?.data ?? {};

    if (payload?.success === false) {
      const error = new Error(payload?.error || payload?.message || `Falha ao acessar ${url}`);
      error.status = response?.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  } catch (error) {
    if (error?.payload) throw error;

    const payload = error?.response?.data || {};
    const status = error?.response?.status;
    const normalizedError = new Error(
      payload?.error ||
      payload?.message ||
      error?.message ||
      `Erro${status ? ` ${status}` : ''} ao acessar ${url}`
    );

    normalizedError.status = status;
    normalizedError.payload = payload;
    throw normalizedError;
  }
}

function createId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

const Icons = {
  Home: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m3 10.5 9-7 9 7"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/></svg>,
  ArrowLeft: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  ChevronLeft: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  ChevronRight: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  Calendar: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  Clock: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  WhatsApp: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.5 8.5 0 0 1-12.7 7.4L3 21l2-5.1A8.5 8.5 0 1 1 21 11.5z"/></svg>,
  User: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  Medical: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  ShoppingBag: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>,
  Message: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/></svg>,
  Check: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  Grip: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="5" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="9" cy="19" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="19" r="1"/></svg>,
  Move: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>,
  Edit: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>,
  Trash: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  X: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  LogOut: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  Refresh: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/><path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/></svg>
};

const TIPOS_COMPROMISSO = [
  { id: 'exame', label: 'Exame de Vista', color: '#1a73e8', bg: '#e8f0fe', icon: <Icons.Medical /> },
  { id: 'cliente', label: 'Serviço ao Cliente', color: '#188038', bg: '#e6f4ea', icon: <Icons.User /> },
  { id: 'retirada', label: 'Retirada de Óculos', color: '#188038', bg: '#e6f4ea', icon: <Icons.ShoppingBag /> },
  { id: 'ajuste', label: 'Ajuste / Manutenção', color: '#f29900', bg: '#fff7df', icon: <Icons.ShoppingBag /> }
];

const STATUS_META = { agendado: { label: 'Agendado', className: 'st-blue' }, confirmado: { label: 'Confirmado', className: 'st-green' }, na_loja: { label: 'Na loja', className: 'st-amber' } };

const TEMPLATE_PADRAO = {
  exame: 'Olá, *{nome}*! Passando para avisar sobre seu *{tipo}* na {loja}, agendado para *{data}* às *{hora}*. Podemos confirmar sua presença?',
  cliente: 'Olá, *{nome}*! Aqui é da {loja}. Você deixou seu contato com a gente e estamos passando para falar sobre seu atendimento. Podemos te ajudar agora?',
  retirada: 'Olá, *{nome}*! Seu compromisso de *{tipo}* está agendado para *{data}* às *{hora}* na {loja}. Estamos te esperando!',
  ajuste: 'Olá, *{nome}*! Lembrete do seu compromisso de *{tipo}* na {loja}, marcado para *{data}* às *{hora}*. Podemos confirmar?'
};


const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const FORM_INICIAL = { nome: '', telefone: '', tipo: 'exame', data: getTodayStr(), hora: '', status: 'agendado', observacao: '', avisarWhatsApp: true };

export default function DashboardAgendamentos() {
  const [agendamentos, setAgendamentos] = useState([]);
  const [dadosLoading, setDadosLoading] = useState(false);
  const [dadosErro, setDadosErro] = useState('');
  const [savingAgenda, setSavingAgenda] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [empresaAtual, setEmpresaAtual] = useState(null);
  const [canaisWhatsapp, setCanaisWhatsapp] = useState([]);
  const [canalSelecionadoId, setCanalSelecionadoId] = useState('');
  const [integracaoLoading, setIntegracaoLoading] = useState(true);
  const [integracaoErro, setIntegracaoErro] = useState('');
  const [viewState, setViewState] = useState('question');
  const [mobileView, setMobileView] = useState('agenda');
  const [selectedDate, setSelectedDate] = useState(getTodayStr());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState(FORM_INICIAL);
  const [editingId, setEditingId] = useState(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(40);
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [movingItemId, setMovingItemId] = useState(null);
  const [moveDraft, setMoveDraft] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [templates, setTemplates] = useState(TEMPLATE_PADRAO);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateKey, setTemplateKey] = useState('exame');
  const [whatsappPreview, setWhatsappPreview] = useState(null);
  const isResizing = useRef(false);
  const splitRef = useRef(null);

  const user = useMemo(() => safeParse('vizumUser', { nomeCompleto: 'Usuário', loja: 'Matriz', tipo: 'admin' }), []);
  const firstName = useMemo(() => getFirstName(user.nomeCompleto), [user.nomeCompleto]);
  const initials = useMemo(() => getInitials(user.nomeCompleto), [user.nomeCompleto]);
  const canalSelecionado = useMemo(() => canaisWhatsapp.find(c => getCanalId(c) === String(canalSelecionadoId || '')) || null, [canaisWhatsapp, canalSelecionadoId]);
  const empresaIdAtual = useMemo(() => getEmpresaIdFromCanal(canalSelecionado) || getEmpresaIdFromUser(user), [canalSelecionado, user]);

  const carregarIntegracaoWhatsapp = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setIntegracaoLoading(true);
    setIntegracaoErro('');
    try {
      // A rota de canais já aplica o escopo do usuário autenticado no backend.
      // Ela deve retornar matriz + filiais permitidas; portanto não filtramos por empresaId aqui.
      const empresaId = getEmpresaIdFromUser(user);
      const canaisPayload = await apiRequest('/api/whatsapp/canais');
      const permitidos = extractCanais(canaisPayload).filter(c => c && c.ativo !== false);
      const enriquecidos = await Promise.all(permitidos.map(async canal => {
        const id = getCanalId(canal);
        let runtime = {};
        try { runtime = id ? await apiRequest(`/api/whatsapp/canais/${encodeURIComponent(id)}/status`) : {}; }
        catch (error) { runtime = { status: canal.status || 'DISCONNECTED', connected: false, statusError: error.message }; }
        const status = String(runtime?.status || canal.status || 'DISCONNECTED').toUpperCase();
        return { ...canal, runtimeStatus: status, runtimeConnected: runtime?.connected === true || status === 'CONNECTED', runtimeAlive: runtime?.runtimeAlive, runtimeQrCode: runtime?.qrCode || null };
      }));
      setCanaisWhatsapp(enriquecidos);
      const storageKey = `lumix_agenda_canal_${empresaId || storageScope(user)}`;
      const salvo = localStorage.getItem(storageKey) || '';
      const escolhido = enriquecidos.find(c => getCanalId(c) === salvo && c.runtimeConnected) || enriquecidos.find(c => c.principal === true && c.runtimeConnected) || enriquecidos.find(c => c.runtimeConnected) || enriquecidos.find(c => getCanalId(c) === salvo) || enriquecidos.find(c => c.principal === true) || enriquecidos[0] || null;
      setCanalSelecionadoId(escolhido ? getCanalId(escolhido) : '');
      if (!enriquecidos.length) setIntegracaoErro('Nenhum canal WhatsApp disponível para o seu grupo de empresas.');
    } catch (error) {
      setCanaisWhatsapp([]);
      setCanalSelecionadoId('');
      setIntegracaoErro(error.message || 'Não foi possível carregar os canais do WhatsApp.');
    } finally { setIntegracaoLoading(false); }
  }, [user]);

  useEffect(() => { void carregarIntegracaoWhatsapp(); }, [carregarIntegracaoWhatsapp]);
  useEffect(() => {
    if (!canalSelecionadoId) return;
    const chaveGrupo = getEmpresaIdFromUser(user) || storageScope(user);
    localStorage.setItem(`lumix_agenda_canal_${chaveGrupo}`, String(canalSelecionadoId));
  }, [canalSelecionadoId, user]);

  useEffect(() => {
    if (!canalSelecionado) {
      const fallbackId = getEmpresaIdFromUser(user);
      setEmpresaAtual(fallbackId ? { _id: fallbackId, nomeFantasia: user.loja || 'Matriz' } : null);
      return;
    }

    const empresaDoCanal = canalSelecionado.empresa && typeof canalSelecionado.empresa === 'object'
      ? canalSelecionado.empresa
      : {
          _id: getEmpresaIdFromCanal(canalSelecionado),
          nomeFantasia: canalSelecionado.empresaNome || canalSelecionado.unidadeNome || canalSelecionado.lojaNome || canalSelecionado.nome || user.loja || 'Empresa'
        };

    setEmpresaAtual(empresaDoCanal);
  }, [canalSelecionado, user]);

  const carregarDadosBanco = useCallback(async ({ silencioso = false } = {}) => {
    if (!empresaIdAtual) {
      setAgendamentos([]);
      setTemplates(TEMPLATE_PADRAO);
      setDadosErro('Não foi possível identificar a empresa desta agenda.');
      return;
    }

    if (!silencioso) setDadosLoading(true);
    setDadosErro('');

    try {
      const [agendaPayload, templatesPayload] = await Promise.all([
        apiRequest('/api/agendamentos', {
          params: { empresaId: empresaIdAtual }
        }),
        apiRequest('/api/agendamentos/mensagens', {
          params: { empresaId: empresaIdAtual }
        })
      ]);

      setAgendamentos(extractAgendamentos(agendaPayload).map(normalizeAgendamento));
      setTemplates({ ...TEMPLATE_PADRAO, ...extractTemplates(templatesPayload) });
    } catch (error) {
      setAgendamentos([]);
      setTemplates(TEMPLATE_PADRAO);
      setDadosErro(error.message || 'Não foi possível carregar os agendamentos desta empresa.');
    } finally {
      if (!silencioso) setDadosLoading(false);
    }
  }, [empresaIdAtual]);

  useEffect(() => {
    void carregarDadosBanco();
  }, [carregarDadosBanco]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 3800);
    return () => clearTimeout(timer);
  }, [notice]);

  const heatmap = useMemo(() => { const map = {}; agendamentos.forEach(a => { map[a.data] = (map[a.data] || 0) + 1; }); return map; }, [agendamentos]);
  const eventosDoDia = useMemo(() => agendamentos.filter(a => a.data === selectedDate).sort((a, b) => String(a.hora || '').localeCompare(String(b.hora || ''))), [agendamentos, selectedDate]);
  const movingItem = useMemo(() => agendamentos.find(a => a.id === movingItemId), [agendamentos, movingItemId]);
  const totalHoje = heatmap[getTodayStr()] || 0;
  const totalMes = useMemo(() => agendamentos.filter(a => { const d = parseLocalDate(a.data); return d.getMonth() === currentMonth && d.getFullYear() === currentYear; }).length, [agendamentos, currentMonth, currentYear]);
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  const resize = useCallback((event) => {
    if (!isResizing.current || !splitRef.current) return;
    const rect = splitRef.current.getBoundingClientRect();
    const clientX = event.clientX;
    const newWidth = ((clientX - rect.left) / rect.width) * 100;
    if (newWidth >= 30 && newWidth <= 54) setLeftPanelWidth(newWidth);
  }, []);

  const stopResizing = useCallback(() => { isResizing.current = false; document.body.classList.remove('lmx-resizing'); }, []);
  const startResizing = useCallback((e) => { e.preventDefault(); isResizing.current = true; document.body.classList.add('lmx-resizing'); }, []);

  useEffect(() => {
    window.addEventListener('pointermove', resize);
    window.addEventListener('pointerup', stopResizing);
    return () => { window.removeEventListener('pointermove', resize); window.removeEventListener('pointerup', stopResizing); document.body.classList.remove('lmx-resizing'); };
  }, [resize, stopResizing]);

  function logout() { document.body.style.opacity = '0'; setTimeout(() => { localStorage.clear(); window.location.href = '/login'; }, 240); }
  function monthPrev() { if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); } else setCurrentMonth(m => m - 1); }
  function monthNext() { if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); } else setCurrentMonth(m => m + 1); }
  function goCurrentMonth() { const today = parseLocalDate(getTodayStr()); setCurrentMonth(today.getMonth()); setCurrentYear(today.getFullYear()); setSelectedDate(getTodayStr()); setFormData(prev => ({ ...prev, data: getTodayStr() })); }
  function getDateFromDay(day) { return `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`; }
  function tipoMeta(tipo) { return TIPOS_COMPROMISSO.find(t => t.id === tipo) || TIPOS_COMPROMISSO[0]; }

  function openMoveDraft(id, targetDate) {
    const item = agendamentos.find(a => a.id === id);
    if (!item || item.data === targetDate) { setMovingItemId(null); setDraggedItemId(null); setDragOverDate(null); return; }
    setMoveDraft({ id, targetDate, alterarHora: false, hora: item.hora, observacao: '' });
    setSelectedDate(targetDate);
    setDragOverDate(null);
  }

  async function confirmMove() {
    if (!moveDraft || savingAgenda) return;
    const original = agendamentos.find(a => a.id === moveDraft.id);
    if (!original) return;

    const horaNova = moveDraft.alterarHora ? moveDraft.hora : original.hora;
    setSavingAgenda(true);

    try {
      const payload = await apiRequest(`/api/agendamentos/${encodeURIComponent(moveDraft.id)}/mover`, {
        method: 'PATCH',
        data: {
          empresaId: empresaIdAtual,
          data: moveDraft.targetDate,
          hora: horaNova,
          observacao: moveDraft.observacao
        }
      });

      const atualizado = normalizeAgendamento(payload?.agendamento || payload?.data || {});
      if (!atualizado.id) throw new Error('O backend não retornou o agendamento atualizado.');

      setAgendamentos(prev => prev.map(a => a.id === moveDraft.id ? atualizado : a));
      setSelectedDate(moveDraft.targetDate);
      setFormData(prev => ({ ...prev, data: moveDraft.targetDate }));
      setMoveDraft(null);
      setMovingItemId(null);
      setDraggedItemId(null);
      setMobileView('agenda');
      setNotice({ type: 'success', text: 'Agendamento remarcado.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Não foi possível remarcar o agendamento.' });
    } finally {
      setSavingAgenda(false);
    }
  }

  function handleSelectDate(day) {
    const date = getDateFromDay(day);
    if (movingItemId) { openMoveDraft(movingItemId, date); return; }
    setSelectedDate(date);
    setFormData(prev => ({ ...prev, data: date }));
  }

  function openForm(kind) {
    setEditingId(null);
    setViewState(kind);
    setMobileView('novo');
    setFormData({ ...FORM_INICIAL, tipo: kind === 'form_consulta' ? 'exame' : 'cliente', data: selectedDate });
  }

  function openEdit(ev) {
    setEditingId(ev.id);
    setViewState(ev.tipo === 'exame' ? 'form_consulta' : 'form_cliente');
    setMobileView('novo');
    setFormData({ nome: ev.nome || '', telefone: ev.telefone || '', tipo: ev.tipo || 'exame', data: ev.data || selectedDate, hora: ev.hora || '', status: ev.status || 'agendado', observacao: ev.observacao || '', avisarWhatsApp: false });
  }

  function cancelForm() {
    setEditingId(null);
    setViewState('question');
    setFormData({ ...FORM_INICIAL, data: selectedDate });
  }

  function handleInputChange(e) {
    const { name, type, checked, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (savingAgenda) return;
    if (!empresaIdAtual) {
      setNotice({ type: 'error', text: 'Empresa da agenda não identificada.' });
      return;
    }

    setSavingAgenda(true);

    try {
      const dados = {
        ...formData,
        empresaId: empresaIdAtual,
        canalWhatsappId: canalSelecionadoId || null
      };

      const payload = editingId
        ? await apiRequest(`/api/agendamentos/${encodeURIComponent(editingId)}`, { method: 'PUT', data: dados })
        : await apiRequest('/api/agendamentos', { method: 'POST', data: dados });

      const salvo = normalizeAgendamento(payload?.agendamento || payload?.data || {});
      if (!salvo.id) throw new Error('O backend não retornou o agendamento salvo.');

      setAgendamentos(prev => editingId
        ? prev.map(a => a.id === editingId ? salvo : a)
        : [...prev, salvo]
      );

      const eraEdicao = Boolean(editingId);
      setSelectedDate(salvo.data);
      setEditingId(null);
      setViewState('question');
      setMobileView('agenda');
      setFormData({ ...FORM_INICIAL, data: salvo.data });
      setNotice({ type: 'success', text: eraEdicao ? 'Agendamento atualizado.' : 'Agendamento criado.' });

      if (!eraEdicao && formData.avisarWhatsApp) openWhatsappPreview(salvo);
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Não foi possível salvar o agendamento.' });
    } finally {
      setSavingAgenda(false);
    }
  }

  function buildMessage(cliente) {
    const tipoObj = tipoMeta(cliente.tipo);
    return applyTemplate(templates[cliente.tipo] || templates.cliente, { ...cliente, loja: empresaLabel(empresaAtual, user) }, tipoObj.label);
  }

  function openWhatsappPreview(cliente) {
    setWhatsappPreview({ cliente, message: buildMessage(cliente) });
  }

  async function confirmWhatsappSend() {
    if (!whatsappPreview?.cliente || sendingWhatsapp) return;
    const phone = normalizePhone(whatsappPreview.cliente.telefone);
    if (!phone) return setNotice({ type: 'error', text: 'WhatsApp inválido.' });
    if (!canalSelecionado) return setNotice({ type: 'error', text: 'Nenhum canal WhatsApp foi encontrado para o seu grupo de empresas.' });
    if (!canalSelecionado.runtimeConnected) return setNotice({ type: 'error', text: `O canal ${canalLabel(canalSelecionado)} não está conectado.` });
    const canalWhatsappId = getCanalId(canalSelecionado);
    if (!canalWhatsappId) return setNotice({ type: 'error', text: 'Canal WhatsApp inválido.' });
    setSendingWhatsapp(true);
    try {
      await apiRequest(`/api/whatsapp/canais/${encodeURIComponent(canalWhatsappId)}/send`, {
        method: 'POST',
        body: JSON.stringify({
          number: phone,
          message: whatsappPreview.message,
          tipoMensagem: 'agendamento',
          origem: 'agendamento',
          empresaId: getEmpresaIdFromCanal(canalSelecionado) || empresaIdAtual || undefined,
          referenceContext: { agendamentoId: whatsappPreview.cliente.id, empresaId: getEmpresaIdFromCanal(canalSelecionado) || empresaIdAtual || undefined, canalWhatsappId, data: whatsappPreview.cliente.data, hora: whatsappPreview.cliente.hora }
        })
      });
      let atualizado = null;
      try {
        const registroPayload = await apiRequest(`/api/agendamentos/${encodeURIComponent(whatsappPreview.cliente.id)}/whatsapp`, {
          method: 'PATCH',
          data: {
            empresaId: empresaIdAtual,
            canalWhatsappId,
            status: 'enviado',
            mensagem: whatsappPreview.message
          }
        });
        atualizado = normalizeAgendamento(registroPayload?.agendamento || registroPayload?.data || {});
      } catch (registroError) {
        console.warn('Mensagem enviada, mas o histórico do agendamento não foi atualizado:', registroError);
      }

      if (atualizado?.id) {
        setAgendamentos(prev => prev.map(item => item.id === atualizado.id ? atualizado : item));
      }

      setWhatsappPreview(null);
      setNotice({ type: 'success', text: `Mensagem enviada por ${canalLabel(canalSelecionado)}.` });
      void carregarIntegracaoWhatsapp({ silencioso: true });
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Falha ao enviar pelo WhatsApp conectado.' });
      void carregarIntegracaoWhatsapp({ silencioso: true });
    } finally { setSendingWhatsapp(false); }
  }

  async function confirmDelete() {
    if (!pendingDelete || savingAgenda) return;
    setSavingAgenda(true);

    try {
      await apiRequest(`/api/agendamentos/${encodeURIComponent(pendingDelete.id)}`, {
        method: 'DELETE',
        params: { empresaId: empresaIdAtual }
      });

      setAgendamentos(prev => prev.filter(a => a.id !== pendingDelete.id));
      if (editingId === pendingDelete.id) cancelForm();
      setPendingDelete(null);
      setNotice({ type: 'success', text: 'Agendamento excluído.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Não foi possível excluir o agendamento.' });
    } finally {
      setSavingAgenda(false);
    }
  }

  async function salvarTemplatesNoBanco() {
    if (!empresaIdAtual || templateSaving) return;
    setTemplateSaving(true);

    try {
      const payload = await apiRequest('/api/agendamentos/mensagens', {
        method: 'PUT',
        data: {
          empresaId: empresaIdAtual,
          templates
        }
      });

      setTemplates({ ...TEMPLATE_PADRAO, ...extractTemplates(payload) });
      setTemplateOpen(false);
      setNotice({ type: 'success', text: 'Modelos de mensagem salvos para esta empresa.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Não foi possível salvar os modelos.' });
    } finally {
      setTemplateSaving(false);
    }
  }

  async function restaurarTemplatesPadrao() {
    if (!empresaIdAtual || templateSaving) return;
    setTemplateSaving(true);

    try {
      const payload = await apiRequest('/api/agendamentos/mensagens/restaurar', {
        method: 'POST',
        data: { empresaId: empresaIdAtual }
      });

      setTemplates({ ...TEMPLATE_PADRAO, ...extractTemplates(payload) });
      setNotice({ type: 'success', text: 'Modelos padrão restaurados.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Não foi possível restaurar os modelos.' });
    } finally {
      setTemplateSaving(false);
    }
  }

  function handleDragStart(e, id) { setDraggedItemId(id); e.dataTransfer.setData('text/plain', id); e.dataTransfer.effectAllowed = 'move'; }
  function handleDragOver(e, date) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverDate(date); }
  function handleDrop(e, date) { e.preventDefault(); const id = e.dataTransfer.getData('text/plain') || draggedItemId; if (id) openMoveDraft(id, date); }
  function handleDragEnd() { setDraggedItemId(null); setDragOverDate(null); }

  return (
    <div className={`agendamentos-wrapper mobile-${mobileView}`} style={{ '--left-panel': `${leftPanelWidth}%` }}>
      <style>{css}</style>

      <header className="gm-app-topbar">
        <div className="gm-topbar-inner">
          <div className="gm-top-left">
            <a href="/home" className="lmx-home-button" title="Voltar para a tela inicial"><Icons.ArrowLeft /><span className="hide-mobile">Tela inicial</span></a>
            <a href="/home" className="gm-brand" aria-label="Lumix Óticas - Home">
              <div className="gm-logo-wrapper"><img src={logoIconPng} alt="Lumix" onError={(e) => { e.currentTarget.style.display = 'none'; }} /></div>
              <span className="gm-brand-text">Lumix <span style={{ color: '#1a73e8' }}>Ó</span><span style={{ color: '#d93025' }}>t</span><span style={{ color: '#fbbc04' }}>i</span><span style={{ color: '#188038' }}>c</span>as</span>
            </a>
            <span className="lmx-page-chip hide-mobile">Agendamentos</span>
          </div>

          <div className="gm-topbar-actions">
            <div className="gm-user-meta hide-mobile"><strong>{firstName}</strong><span>{empresaLabel(empresaAtual, user)}</span></div>
            <div className="gm-avatar" title={user.nomeCompleto}>{initials}</div>
            <button type="button" className="gm-btn-logout" onClick={logout} title="Sair do sistema"><Icons.LogOut /><span className="hide-mobile">Sair</span></button>
          </div>
        </div>
      </header>

      {notice && (
        <div className={`lmx-notice notice-${notice.type || 'info'}`} role="status">
          {notice.text}
        </div>
      )}

      <div className="lmx-mobile-tabs">
        <button type="button" className={mobileView === 'agenda' ? 'active' : ''} onClick={() => setMobileView('agenda')}><Icons.Calendar /> Agenda</button>
        <button type="button" className={mobileView === 'novo' ? 'active' : ''} onClick={() => { setMobileView('novo'); setViewState('question'); }}><Icons.Check /> Novo</button>
      </div>

      <main className="lmx-split-layout" ref={splitRef}>
        <section className="lmx-form-panel">
          <div className="lmx-form-inner">
            {viewState === 'question' ? (
              <div className="lmx-question-state fade-in">
                <span className="lmx-eyebrow">Agenda</span>
                <h1 className="lmx-hero-title">O que você quer agendar?</h1>
                <p className="lmx-hero-sub">Crie compromissos rápidos para exame de vista ou atendimento ao cliente. Os compromissos ficam salvos e podem ser criados, editados, movidos ou excluídos.</p>

                <div className="lmx-stats-row">
                  <div><strong>{totalHoje}</strong><span>Hoje</span></div>
                  <div><strong>{totalMes}</strong><span>No mês</span></div>
                  <div><strong>{eventosDoDia.length}</strong><span>Selecionado</span></div>
                </div>

                <div className="lmx-choice-stack">
                  <button type="button" className="lmx-choice-card btn-blue" onClick={() => openForm('form_consulta')}>
                    <div className="choice-icon"><Icons.Medical /></div><div className="choice-copy"><strong>Exame de vista</strong><span>Cliente interessado em consulta, exame ou retorno.</span></div><div className="choice-arrow"><Icons.ChevronRight /></div>
                  </button>
                  <button type="button" className="lmx-choice-card btn-green" onClick={() => openForm('form_cliente')}>
                    <div className="choice-icon"><Icons.User /></div><div className="choice-copy"><strong>Serviço ao cliente</strong><span>Contato espontâneo, aviso, retirada ou manutenção.</span></div><div className="choice-arrow"><Icons.ChevronRight /></div>
                  </button>
                </div>
              </div>
            ) : (
              <div className="lmx-form-state slide-in-left">
                <button type="button" className="lmx-btn-back" onClick={cancelForm}><Icons.ArrowLeft /> Voltar</button>
                <h2 className="lmx-form-title">{editingId ? 'Editar agendamento' : viewState === 'form_consulta' ? 'Novo exame' : 'Novo atendimento'}</h2>

                <form className="lmx-agenda-form" onSubmit={handleSubmit}>
                  <div className="lmx-form-group"><label>Paciente / Cliente</label><div className="lmx-input-box"><Icons.User /><input type="text" name="nome" placeholder="Nome completo" required value={formData.nome} onChange={handleInputChange} autoFocus /></div></div>
                  <div className="lmx-form-row">
                    <div className="lmx-form-group"><label>WhatsApp</label><div className="lmx-input-box"><Icons.WhatsApp /><input type="tel" name="telefone" placeholder="(00) 00000-0000" required value={formData.telefone} onChange={handleInputChange} /></div></div>
                    <div className="lmx-form-group"><label>Tipo</label><select name="tipo" className="lmx-input-box select-box" value={formData.tipo} onChange={handleInputChange}>{viewState === 'form_consulta' ? <option value="exame">Exame de Vista</option> : <><option value="cliente">Serviço ao Cliente</option><option value="retirada">Retirada de Óculos</option><option value="ajuste">Ajuste / Manutenção</option></>}</select></div>
                  </div>
                  <div className="lmx-form-row">
                    <div className="lmx-form-group"><label>Data</label><input type="date" name="data" required className="lmx-input-box native-field" value={formData.data} onChange={handleInputChange} /></div>
                    <div className="lmx-form-group"><label>Horário</label><input type="time" name="hora" required className="lmx-input-box native-field" value={formData.hora} onChange={handleInputChange} /></div>
                  </div>
                  <div className="lmx-form-row">
                    <div className="lmx-form-group"><label>Status</label><select name="status" className="lmx-input-box select-box" value={formData.status} onChange={handleInputChange}><option value="agendado">Agendado</option><option value="confirmado">Confirmado</option><option value="na_loja">Na loja</option></select></div>
                    <div className="lmx-form-group"><label>Observação</label><input name="observacao" className="lmx-input-box native-field" placeholder="Opcional" value={formData.observacao} onChange={handleInputChange} /></div>
                  </div>
                  {!editingId && <label className="lmx-checkbox-wrap"><input type="checkbox" name="avisarWhatsApp" checked={formData.avisarWhatsApp} onChange={handleInputChange} /><span className="checkbox-custom"><Icons.Check /></span><span className="checkbox-label">Preparar aviso no WhatsApp ao salvar</span></label>}
                  <button type="submit" className="lmx-btn-submit" disabled={savingAgenda}>{savingAgenda ? 'Salvando...' : editingId ? 'Salvar alterações' : 'Confirmar agendamento'} <Icons.Check /></button>
                </form>
              </div>
            )}
          </div>
        </section>

        <div className="lmx-resizer-handle desktop-only" onPointerDown={startResizing}><div className="resizer-line"><div className="resizer-icon"><Icons.Grip /></div></div></div>

        <section className="lmx-calendar-panel">
          <div className="lmx-calendar-inner custom-scrollbar">
            <div className="lmx-panel-head">
              <div><span className="lmx-eyebrow">Calendário</span><h2>Compromissos</h2><p>{empresaLabel(empresaAtual, user)} · avisos pelo canal WhatsApp selecionado.</p></div>
              <div className="lmx-panel-actions">
                <div className={`lmx-channel-control ${canalSelecionado?.runtimeConnected ? 'is-connected' : ''}`}>
                  <span className="channel-dot" />
                  <select value={canalSelecionadoId} onChange={(e) => setCanalSelecionadoId(e.target.value)} disabled={integracaoLoading || !canaisWhatsapp.length} title={integracaoErro || 'Canal usado nos avisos'}>
                    {!canaisWhatsapp.length && <option value="">{integracaoLoading ? 'Carregando canais...' : 'Sem canal disponível'}</option>}
                    {canaisWhatsapp.map(canal => <option key={getCanalId(canal)} value={getCanalId(canal)}>{canalLabel(canal)} · {canal.runtimeConnected ? 'Conectado' : canal.runtimeStatus || 'Desconectado'}</option>)}
                  </select>
                  <button type="button" onClick={() => void carregarIntegracaoWhatsapp()} disabled={integracaoLoading} title="Atualizar canais"><Icons.Refresh /></button>
                </div>
                <button type="button" className="template-button" onClick={() => setTemplateOpen(true)}><Icons.Message /> Modelo de mensagem</button>
              </div>
            </div>
            {integracaoErro && <div className="lmx-integration-warning">{integracaoErro}</div>}
            {dadosErro && <div className="lmx-integration-warning">{dadosErro}</div>}
            {dadosLoading && <div className="lmx-integration-warning">Carregando agenda desta empresa...</div>}

            {movingItem && (
              <div className="lmx-move-banner">
                <div><strong>Movendo {movingItem.nome}</strong><span>Toque no novo dia. Depois você confirma observação e horário.</span></div>
                <button type="button" onClick={() => setMovingItemId(null)}><Icons.X /> Cancelar</button>
              </div>
            )}

            <div className={`lmx-calendar-widget ${movingItem ? 'is-moving' : ''}`}>
              <div className="cal-header">
                <h3>{MESES[currentMonth]} {currentYear}</h3>
                <div className="cal-nav">
                  <button type="button" className="today-button" onClick={goCurrentMonth}>Mês atual</button>
                  <button type="button" onClick={monthPrev}><Icons.ChevronLeft /></button>
                  <button type="button" onClick={monthNext}><Icons.ChevronRight /></button>
                </div>
              </div>
              <div className="cal-grid">
                {DIAS_SEMANA.map(d => <div key={d} className="cal-day-name">{d}</div>)}
                {Array.from({ length: firstDayOfMonth }).map((_, i) => <div key={`empty-${i}`} className="cal-cell empty" />)}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const date = getDateFromDay(day);
                  const eventCount = heatmap[date] || 0;
                  const isSelected = selectedDate === date;
                  const isToday = getTodayStr() === date;
                  const heatClass = eventCount >= 3 ? 'heat-high' : eventCount > 0 ? 'heat-low' : '';
                  return (
                    <button type="button" key={day} className={`cal-cell ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${heatClass} ${dragOverDate === date ? 'drag-over' : ''} ${movingItem ? 'move-target' : ''}`} onClick={() => handleSelectDate(day)} onDragOver={(e) => handleDragOver(e, date)} onDragLeave={() => setDragOverDate(null)} onDrop={(e) => handleDrop(e, date)}>
                      <span className="cal-number">{day}</span>{eventCount > 0 && <span className="cal-count">{eventCount}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="agenda-mobile-tools">
              <div className={`lmx-channel-control mobile-channel ${canalSelecionado?.runtimeConnected ? 'is-connected' : ''}`}>
                <span className="channel-dot" />
                <select value={canalSelecionadoId} onChange={(e) => setCanalSelecionadoId(e.target.value)} disabled={integracaoLoading || !canaisWhatsapp.length}>
                  {!canaisWhatsapp.length && <option value="">{integracaoLoading ? 'Carregando WhatsApp...' : 'Sem canal WhatsApp'}</option>}
                  {canaisWhatsapp.map(canal => <option key={getCanalId(canal)} value={getCanalId(canal)}>{canalLabel(canal)} · {canal.runtimeConnected ? 'Conectado' : canal.runtimeStatus || 'Desconectado'}</option>)}
                </select>
                <button type="button" onClick={() => void carregarIntegracaoWhatsapp()} disabled={integracaoLoading} title="Atualizar"><Icons.Refresh /></button>
              </div>
              {integracaoErro && <small className="channel-error">{integracaoErro}</small>}
              <button type="button" className="template-button" onClick={() => setTemplateOpen(true)}><Icons.Message /> Modelo de mensagem</button>
            </div>

            <div className="lmx-agenda-list-wrapper">
              <div className="agenda-header">
                <div><h4>Agenda de {formatDateBR(selectedDate, { weekday: 'long', day: '2-digit', month: 'long' })}</h4><span>{eventosDoDia.length ? `${eventosDoDia.length} compromisso${eventosDoDia.length > 1 ? 's' : ''}` : 'Dia livre'}</span></div>
                <button type="button" className="agenda-add" onClick={() => { setMobileView('novo'); setViewState('question'); }}><Icons.Check /> Novo</button>
              </div>

              <div className="agenda-list">
                {eventosDoDia.length === 0 ? (
                  <div className="agenda-empty"><Icons.Calendar /><strong>Nenhum compromisso</strong><p>Use “Novo” para adicionar um compromisso nesta data.</p></div>
                ) : eventosDoDia.map(ev => {
                  const tipoObj = tipoMeta(ev.tipo);
                  const status = STATUS_META[ev.status] || STATUS_META.agendado;
                  const isDragging = draggedItemId === ev.id;
                  const isMoving = movingItemId === ev.id;
                  return (
                    <div className={`agenda-item ${isDragging ? 'is-dragging' : ''} ${isMoving ? 'is-moving' : ''}`} key={ev.id} draggable onDragStart={(e) => handleDragStart(e, ev.id)} onDragEnd={handleDragEnd}>
                      <div className="agenda-time"><Icons.Clock />{ev.hora}</div>
                      <div className="agenda-card">
                        <div className="drag-indicator" title="Arraste no desktop"><Icons.Grip /></div>
                        <div className="agenda-type-icon" style={{ color: tipoObj.color, background: tipoObj.bg }}>{tipoObj.icon}</div>
                        <div className="agenda-info-wrap">
                          <div className="agenda-info">
                            <strong>{ev.nome}</strong><span style={{ color: tipoObj.color }}>{tipoObj.label}</span><small className={`status-chip ${status.className}`}>{status.label}</small>
                            {ev.observacao && <em>{ev.observacao}</em>}
                            {!!ev.movimentacoes?.length && <small className="move-history">Movido {ev.movimentacoes.length}x</small>}
                          </div>
                          <div className="agenda-actions">
                            <button type="button" className="btn-move-line" onClick={() => setMovingItemId(ev.id)}><Icons.Move /><span>Mover</span></button>
                            <button type="button" className="btn-edit-line" onClick={() => openEdit(ev)}><Icons.Edit /><span>Editar</span></button>
                            <button type="button" className="btn-wa-line" onClick={() => openWhatsappPreview(ev)}><Icons.WhatsApp /><span>Avisar compromisso</span></button>
                            <button type="button" className="btn-delete-line" onClick={() => setPendingDelete(ev)}><Icons.Trash /><span>Excluir</span></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </main>

      {moveDraft && <div className="lmx-modal-backdrop" role="dialog" aria-modal="true">
        <div className="lmx-modal-card">
          <div className="modal-head"><div><span className="lmx-eyebrow">Movimentação</span><h3>Confirmar nova data</h3></div><button type="button" onClick={() => setMoveDraft(null)}><Icons.X /></button></div>
          <p className="modal-copy">Você está movendo <strong>{agendamentos.find(a => a.id === moveDraft.id)?.nome}</strong> para <strong>{formatDateBR(moveDraft.targetDate)}</strong>.</p>
          <label className="lmx-checkbox-wrap modal-check"><input type="checkbox" checked={moveDraft.alterarHora} onChange={(e) => setMoveDraft(prev => ({ ...prev, alterarHora: e.target.checked }))} /><span className="checkbox-custom"><Icons.Check /></span><span className="checkbox-label">Alterar horário também</span></label>
          {moveDraft.alterarHora && <div className="lmx-form-group"><label>Novo horário</label><input type="time" className="lmx-input-box native-field" value={moveDraft.hora} onChange={(e) => setMoveDraft(prev => ({ ...prev, hora: e.target.value }))} /></div>}
          <div className="lmx-form-group"><label>Observação da mudança</label><textarea className="lmx-textarea" placeholder="Ex: cliente pediu para remarcar porque não pode vir neste horário." value={moveDraft.observacao} onChange={(e) => setMoveDraft(prev => ({ ...prev, observacao: e.target.value }))} /></div>
          <div className="modal-actions"><button type="button" className="btn-light" onClick={() => setMoveDraft(null)}>Cancelar</button><button type="button" className="btn-primary" disabled={savingAgenda} onClick={() => void confirmMove()}>{savingAgenda ? 'Salvando...' : 'Confirmar mudança'}</button></div>
        </div>
      </div>}

      {templateOpen && <div className="lmx-modal-backdrop" role="dialog" aria-modal="true">
        <div className="lmx-modal-card modal-wide">
          <div className="modal-head"><div><span className="lmx-eyebrow">WhatsApp</span><h3>Modelo de mensagem</h3></div><button type="button" onClick={() => setTemplateOpen(false)}><Icons.X /></button></div>
          <p className="modal-copy">Defina uma mensagem por tipo de compromisso. Você pode usar: <strong>{'{nome}'}</strong>, <strong>{'{data}'}</strong>, <strong>{'{hora}'}</strong>, <strong>{'{tipo}'}</strong> e <strong>{'{loja}'}</strong>.</p>
          <div className="lmx-form-group"><label>Tipo de compromisso</label><select className="lmx-input-box select-box" value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>{TIPOS_COMPROMISSO.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}</select></div>
          <div className="lmx-form-group"><label>Mensagem configurada</label><textarea className="lmx-textarea template-area" value={templates[templateKey] || ''} onChange={(e) => setTemplates(prev => ({ ...prev, [templateKey]: e.target.value }))} /></div>
          <div className="modal-actions"><button type="button" className="btn-light" disabled={templateSaving} onClick={() => void restaurarTemplatesPadrao()}>{templateSaving ? 'Salvando...' : 'Restaurar padrão'}</button><button type="button" className="btn-primary" disabled={templateSaving} onClick={() => void salvarTemplatesNoBanco()}>{templateSaving ? 'Salvando...' : 'Salvar modelo'}</button></div>
        </div>
      </div>}

      {whatsappPreview && <div className="lmx-modal-backdrop" role="dialog" aria-modal="true">
        <div className="lmx-modal-card modal-wide">
          <div className="modal-head"><div><span className="lmx-eyebrow">Aviso ao cliente</span><h3>Confirmar mensagem</h3></div><button type="button" onClick={() => setWhatsappPreview(null)}><Icons.X /></button></div>
          <p className="modal-copy">Confira a mensagem antes de enviar para <strong>{whatsappPreview.cliente.nome}</strong>. Canal: <strong>{canalSelecionado ? canalLabel(canalSelecionado) : 'nenhum canal disponível'}</strong>{canalSelecionado && !canalSelecionado.runtimeConnected ? ' (desconectado)' : ''}.</p>
          <div className="lmx-form-group"><label>Mensagem que será enviada</label><textarea className="lmx-textarea template-area" value={whatsappPreview.message} onChange={(e) => setWhatsappPreview(prev => ({ ...prev, message: e.target.value }))} /></div>
          <div className="modal-actions"><button type="button" className="btn-light" onClick={() => setWhatsappPreview(null)}>Cancelar</button><button type="button" className="btn-primary btn-whatsapp" onClick={confirmWhatsappSend} disabled={sendingWhatsapp || !canalSelecionado?.runtimeConnected}><Icons.WhatsApp /> {sendingWhatsapp ? 'Enviando...' : canalSelecionado?.runtimeConnected ? 'Enviar pelo WhatsApp' : 'Canal desconectado'}</button></div>
        </div>
      </div>}

      {pendingDelete && <div className="lmx-modal-backdrop" role="dialog" aria-modal="true">
        <div className="lmx-modal-card">
          <div className="modal-head"><div><span className="lmx-eyebrow danger">Excluir</span><h3>Excluir agendamento?</h3></div><button type="button" onClick={() => setPendingDelete(null)}><Icons.X /></button></div>
          <p className="modal-copy">Essa ação vai remover o compromisso de <strong>{pendingDelete.nome}</strong> da agenda.</p>
          <div className="modal-actions"><button type="button" className="btn-light" onClick={() => setPendingDelete(null)}>Cancelar</button><button type="button" className="btn-danger" disabled={savingAgenda} onClick={() => void confirmDelete()}>{savingAgenda ? 'Excluindo...' : 'Excluir'}</button></div>
        </div>
      </div>}
    </div>
  );
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

:root{
  --bg:#ffffff;
  --surface:#ffffff;
  --soft-bg:#f7f9f9;
  --line:#e8ecec;
  --line-strong:#d8dfde;
  --text:#172020;
  --text-2:#374642;
  --muted:#74807d;
  --soft:#9aa3a1;
  --accent:#36564c;
  --accent-soft:#eef4f2;
  --green:#188038;
  --green-soft:#edf7f0;
  --red:#a63e36;
  --red-soft:#fff6f5;
  --amber:#9a6a10;
  --amber-soft:#fff9eb;
  --blue:#315f8f;
  --blue-soft:#f0f5fa;
  --font:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
}

*,*::before,*::after{box-sizing:border-box}
html,body,#root{margin:0;min-height:100%;font-family:var(--font)}
body{
  background:var(--bg);
  color:var(--text);
  -webkit-font-smoothing:antialiased;
  text-rendering:optimizeLegibility;
  -webkit-tap-highlight-color:transparent;
  transition:opacity .2s ease;
}
a{color:inherit;text-decoration:none}
button,input,select,textarea{font:inherit}
button{cursor:pointer}
button:disabled{cursor:not-allowed;opacity:.6}
.hide-mobile,.desktop-only{display:flex}
.show-mobile{display:none}

.agendamentos-wrapper{
  min-height:100vh;
  height:100vh;
  display:flex;
  flex-direction:column;
  overflow:hidden;
  background:#fff;
}

.gm-app-topbar{
  position:relative;
  z-index:50;
  height:60px;
  flex:0 0 60px;
  background:#fff;
  border-bottom:1px solid var(--line);
}
.gm-topbar-inner{
  width:min(1240px,calc(100% - 40px));
  height:100%;
  margin:0 auto;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:20px;
}
.gm-top-left,.gm-brand,.gm-topbar-actions,.lmx-home-button{display:flex;align-items:center}
.gm-top-left{gap:8px;min-width:0}
.gm-brand{gap:8px;min-width:0}
.gm-logo-wrapper{width:28px;height:28px;display:grid;place-items:center;flex:0 0 auto}
.gm-logo-wrapper img{width:22px;height:22px;object-fit:contain}
.gm-brand-text{font-size:15px;line-height:1;font-weight:800;letter-spacing:-.035em;white-space:nowrap}
.lmx-home-button{
  height:31px;
  padding:0 8px;
  gap:5px;
  border:1px solid var(--line);
  border-radius:7px;
  background:#fff;
  color:var(--muted);
  font-size:9px;
  font-weight:700;
}
.lmx-home-button:hover{color:var(--accent);border-color:var(--line-strong);background:var(--soft-bg)}
.lmx-home-button svg{width:13px;height:13px}
.lmx-page-chip{
  height:25px;
  align-items:center;
  padding:0 8px;
  border-left:1px solid var(--line);
  color:var(--muted);
  font-size:9px;
  font-weight:700;
  text-transform:uppercase;
  letter-spacing:.05em;
}
.gm-topbar-actions{gap:9px}
.gm-user-meta{flex-direction:column;align-items:flex-end;justify-content:center}
.gm-user-meta strong{font-size:10.5px;line-height:1.2;font-weight:700}
.gm-user-meta span{margin-top:2px;color:var(--muted);font-size:8px;line-height:1;font-weight:650;text-transform:uppercase;letter-spacing:.05em}
.gm-avatar{
  width:31px;height:31px;display:grid;place-items:center;
  border:1px solid var(--line-strong);border-radius:50%;
  background:#fff;color:var(--text-2);font-size:9px;font-weight:800;
}
.gm-btn-logout{
  width:auto;height:31px;padding:0 8px;display:inline-flex;align-items:center;gap:5px;
  border:1px solid var(--line);border-radius:7px;background:#fff;color:var(--muted);
  font-size:9px;font-weight:700;
}
.gm-btn-logout:hover{color:var(--red);border-color:#ead8d5;background:var(--red-soft)}
.gm-btn-logout svg{width:13px;height:13px}

.lmx-notice{
  position:fixed;
  top:70px;
  left:50%;
  z-index:300;
  transform:translateX(-50%);
  max-width:min(520px,calc(100% - 24px));
  padding:10px 13px;
  border:1px solid var(--line-strong);
  background:#fff;
  color:var(--text-2);
  font-size:11px;
  font-weight:650;
}
.notice-success{border-color:#cfe5d5;color:#23643a;background:#fbfefc}
.notice-error{border-color:#ecd3d0;color:var(--red);background:#fffafa}
.notice-info{border-color:#d7e2ec;color:var(--blue);background:#fbfdff}

.lmx-mobile-tabs{display:none}

.lmx-split-layout{flex:1;min-height:0;display:flex;overflow:hidden}
.lmx-form-panel{
  flex:0 0 var(--left-panel);
  min-width:340px;
  background:#fff;
  border-right:1px solid var(--line);
  overflow:auto;
}
.lmx-form-inner{
  min-height:100%;
  max-width:520px;
  margin:0 auto;
  padding:28px 30px;
  display:flex;
  align-items:center;
}
.lmx-question-state,.lmx-form-state{width:100%}
.lmx-eyebrow{
  display:block;
  margin-bottom:7px;
  color:var(--muted);
  font-size:8.5px;
  line-height:1;
  font-weight:700;
  text-transform:uppercase;
  letter-spacing:.06em;
}
.lmx-eyebrow.danger{color:var(--red)}
.lmx-hero-title{
  margin:0 0 5px;
  font-size:23px;
  line-height:1.15;
  font-weight:750;
  letter-spacing:-.035em;
}
.lmx-hero-sub{
  margin:0 0 22px;
  color:var(--muted);
  font-size:11px;
  line-height:1.55;
  font-weight:500;
}
.lmx-stats-row{
  display:grid;
  grid-template-columns:repeat(3,1fr);
  margin-bottom:20px;
  border-top:1px solid var(--line);
  border-bottom:1px solid var(--line);
}
.lmx-stats-row div{padding:11px 9px;border-right:1px solid var(--line)}
.lmx-stats-row div:last-child{border-right:0}
.lmx-stats-row strong{display:block;font-size:16px;font-weight:750}
.lmx-stats-row span{display:block;margin-top:2px;color:var(--muted);font-size:8px;font-weight:600}

.lmx-choice-stack{display:grid;border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.lmx-choice-card{
  width:100%;
  min-height:72px;
  padding:10px 4px;
  display:grid;
  grid-template-columns:31px minmax(0,1fr) 15px;
  align-items:center;
  gap:10px;
  border:0;
  border-bottom:1px solid var(--line);
  background:#fff;
  text-align:left;
}
.lmx-choice-card:last-child{border-bottom:0}
.lmx-choice-card:hover{background:var(--soft-bg)}
.choice-icon{
  width:31px;height:31px;display:grid;place-items:center;
  background:transparent!important;color:var(--accent)!important;
}
.choice-icon svg{width:17px;height:17px}
.choice-copy{min-width:0}
.choice-copy strong{display:block;font-size:10.5px;font-weight:750}
.choice-copy span{display:block;margin-top:3px;color:var(--muted);font-size:8.5px;line-height:1.4;font-weight:500}
.choice-arrow{display:grid;place-items:center;color:#b7bfbd}
.choice-arrow svg{width:14px;height:14px}
.lmx-choice-card:hover .choice-arrow{color:var(--accent)}

.lmx-btn-back{
  margin:0 0 18px;
  padding:0;
  display:inline-flex;
  align-items:center;
  gap:6px;
  border:0;
  background:transparent;
  color:var(--muted);
  font-size:9px;
  font-weight:700;
}
.lmx-btn-back:hover{color:var(--accent)}
.lmx-btn-back svg{width:13px;height:13px}
.lmx-form-title{margin:0 0 18px;font-size:18px;font-weight:750;letter-spacing:-.025em}
.lmx-agenda-form{display:grid;gap:13px}
.lmx-form-row{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.lmx-form-group{display:grid;gap:5px}
.lmx-form-group label{
  color:var(--muted);
  font-size:8px;
  line-height:1;
  font-weight:700;
  text-transform:uppercase;
  letter-spacing:.05em;
}
.lmx-input-box,.lmx-textarea{
  width:100%;
  border:1px solid var(--line-strong);
  border-radius:7px;
  background:#fff;
  color:var(--text);
  outline:0;
}
.lmx-input-box{
  height:38px;
  padding:0 10px;
  display:flex;
  align-items:center;
  gap:7px;
  font-size:10.5px;
  font-weight:600;
}
.lmx-input-box svg{width:14px;height:14px;color:var(--muted);flex:0 0 auto}
.lmx-input-box input{width:100%;border:0;background:transparent;outline:0;color:var(--text);font:inherit}
.native-field{display:block}
.select-box{appearance:auto}
.lmx-textarea{
  min-height:84px;
  padding:10px;
  resize:vertical;
  font-size:10.5px;
  line-height:1.45;
  font-weight:500;
}
.template-area{min-height:130px}
.lmx-input-box:focus-within,.native-field:focus,.select-box:focus,.lmx-textarea:focus{border-color:var(--accent)}
.lmx-checkbox-wrap{display:flex;align-items:center;gap:8px;margin-top:2px;cursor:pointer}
.lmx-checkbox-wrap input{display:none}
.checkbox-custom{
  width:16px;height:16px;display:grid;place-items:center;
  border:1px solid var(--line-strong);border-radius:4px;background:#fff;
}
.checkbox-custom svg{width:10px;height:10px;color:#fff;opacity:0}
.lmx-checkbox-wrap input:checked+.checkbox-custom{background:var(--accent);border-color:var(--accent)}
.lmx-checkbox-wrap input:checked+.checkbox-custom svg{opacity:1}
.checkbox-label{font-size:9px;font-weight:600;color:var(--text-2)}
.lmx-btn-submit{
  height:38px;
  display:flex;align-items:center;justify-content:center;gap:6px;
  border:1px solid var(--accent);border-radius:7px;
  background:var(--accent);color:#fff;
  font-size:10px;font-weight:700;
}
.lmx-btn-submit:hover{filter:brightness(.95)}
.lmx-btn-submit svg{width:13px;height:13px}

.lmx-resizer-handle{
  width:10px;
  margin:0 -5px;
  z-index:20;
  align-items:center;
  justify-content:center;
  cursor:col-resize;
}
.resizer-line{width:1px;height:100%;background:var(--line)}
.resizer-icon{display:none}

.lmx-calendar-panel{
  flex:1;
  min-width:0;
  padding:26px 28px 24px;
  overflow:hidden;
  background:#fff;
}
.lmx-calendar-inner{
  height:100%;
  max-width:800px;
  margin:0 auto;
  overflow:auto;
  padding-right:5px;
}
.custom-scrollbar::-webkit-scrollbar{width:5px}
.custom-scrollbar::-webkit-scrollbar-thumb{background:#dfe4e3}
.lmx-panel-head{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:16px;
  margin-bottom:15px;
  padding-bottom:15px;
  border-bottom:1px solid var(--line);
}
.lmx-panel-head h2{margin:0;font-size:18px;font-weight:750;letter-spacing:-.025em}
.lmx-panel-head p{max-width:540px;margin:4px 0 0;color:var(--muted);font-size:9px;line-height:1.5;font-weight:500}
.template-button,.agenda-add{
  height:31px;
  padding:0 9px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:5px;
  border:1px solid var(--line);
  border-radius:7px;
  background:#fff;
  color:var(--muted);
  font-size:9px;
  font-weight:700;
  white-space:nowrap;
}
.template-button:hover,.agenda-add:hover{color:var(--accent);border-color:var(--line-strong);background:var(--soft-bg)}
.template-button svg,.agenda-add svg{width:13px;height:13px}
.agenda-mobile-tools{display:none}

.lmx-move-banner{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  margin-bottom:12px;
  padding:10px 12px;
  border:1px solid #cfdad7;
  background:var(--accent-soft);
}
.lmx-move-banner strong{display:block;font-size:10px;font-weight:750}
.lmx-move-banner span{display:block;margin-top:2px;color:var(--muted);font-size:8.5px}
.lmx-move-banner button{
  height:29px;padding:0 8px;display:flex;align-items:center;gap:5px;
  border:1px solid var(--line-strong);border-radius:6px;background:#fff;color:var(--muted);
  font-size:8.5px;font-weight:700;
}
.lmx-move-banner svg{width:12px;height:12px}

.lmx-calendar-widget{
  margin-bottom:18px;
  padding:15px 0 0;
  border-top:1px solid var(--line);
  border-bottom:1px solid var(--line);
  background:#fff;
}
.cal-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  padding:0 0 12px;
}
.cal-header h3{margin:0;font-size:12px;font-weight:750;text-transform:capitalize}
.cal-nav{display:flex;align-items:center;gap:5px}
.cal-nav button{
  height:29px;min-width:29px;padding:0 7px;
  display:grid;place-items:center;
  border:1px solid var(--line);
  border-radius:6px;
  background:#fff;color:var(--muted);
  font-size:8.5px;font-weight:700;
}
.cal-nav button:hover{color:var(--accent);background:var(--soft-bg)}
.cal-nav .today-button{padding:0 9px}
.cal-nav svg{width:13px;height:13px}
.cal-grid{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:0}
.cal-day-name{
  padding:6px 0;
  text-align:center;
  border-bottom:1px solid var(--line);
  color:var(--muted);
  font-size:7.5px;
  font-weight:700;
  text-transform:uppercase;
}
.cal-cell{
  position:relative;
  min-height:48px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  border:0;
  border-right:1px solid var(--line);
  border-bottom:1px solid var(--line);
  background:#fff;
  color:var(--text-2);
  font-size:10px;
  font-weight:650;
}
.cal-cell:nth-child(7n){border-right:0}
.cal-cell.empty{background:#fff;cursor:default}
.cal-cell:not(.empty):hover{background:var(--soft-bg)}
.cal-cell.today{color:var(--accent);font-weight:800}
.cal-cell.selected{background:var(--accent)!important;color:#fff!important}
.cal-cell.drag-over,.cal-cell.move-target:hover{background:var(--accent-soft)!important;color:var(--accent)!important;outline:1px solid var(--accent);outline-offset:-1px}
.cal-number{position:relative;z-index:2}
.cal-count{
  position:absolute;
  top:5px;
  right:5px;
  min-width:14px;
  height:14px;
  padding:0 3px;
  display:grid;
  place-items:center;
  border-radius:7px;
  background:var(--soft-bg);
  color:var(--muted);
  font-size:7px;
  font-weight:700;
}
.cal-cell.selected .cal-count{background:rgba(255,255,255,.18);color:#fff}
.cal-cell.heat-low:not(.selected) .cal-count{background:var(--amber-soft);color:var(--amber)}
.cal-cell.heat-high:not(.selected) .cal-count{background:var(--red-soft);color:var(--red)}

.agenda-header{
  margin:16px 0 7px;
  display:flex;
  align-items:flex-end;
  justify-content:space-between;
  gap:12px;
}
.agenda-header h4{margin:0;font-size:11px;font-weight:750;text-transform:capitalize}
.agenda-header span{display:block;margin-top:2px;color:var(--muted);font-size:8px}
.agenda-list{
  display:grid;
  gap:0;
  padding-bottom:24px;
  border-top:1px solid var(--line);
  border-bottom:1px solid var(--line);
}
.agenda-empty{
  padding:30px 16px;
  text-align:center;
  color:var(--muted);
}
.agenda-empty svg{width:24px;height:24px;margin-bottom:7px;opacity:.5}
.agenda-empty strong{display:block;color:var(--text);font-size:10px;font-weight:750}
.agenda-empty p{margin:3px 0 0;font-size:8.5px;line-height:1.4}

.agenda-item{
  display:grid;
  grid-template-columns:58px 1fr;
  gap:10px;
  align-items:stretch;
  border-bottom:1px solid var(--line);
}
.agenda-item:last-child{border-bottom:0}
.agenda-item.is-dragging{opacity:.45}
.agenda-time{
  padding-top:14px;
  display:flex;
  align-items:flex-start;
  justify-content:flex-end;
  gap:4px;
  color:var(--muted);
  font-size:9px;
  font-weight:700;
}
.agenda-time svg{width:11px;height:11px}
.agenda-card{
  min-width:0;
  padding:10px 4px;
  display:flex;
  align-items:center;
  gap:9px;
  background:#fff;
  cursor:grab;
}
.agenda-item.is-moving .agenda-card{background:var(--accent-soft)}
.drag-indicator{display:flex;color:#c5ccca}
.drag-indicator svg{width:14px;height:14px}
.agenda-type-icon{
  width:30px;height:30px;display:grid;place-items:center;flex:0 0 auto;
  background:transparent!important;
  color:var(--accent)!important;
}
.agenda-type-icon svg{width:15px;height:15px}
.agenda-info-wrap{
  min-width:0;
  flex:1;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
}
.agenda-info{min-width:0}
.agenda-info strong{
  display:block;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  font-size:10px;
  font-weight:750;
}
.agenda-info span{display:block;margin-top:2px;color:var(--muted)!important;font-size:7.5px;font-weight:650;text-transform:uppercase;letter-spacing:.04em}
.agenda-info em{display:block;margin-top:4px;color:var(--muted);font-size:8px;line-height:1.35;font-style:normal}
.status-chip,.move-history{
  display:inline-flex!important;
  height:18px!important;
  margin-top:5px!important;
  padding:0 6px!important;
  align-items:center!important;
  border:1px solid var(--line)!important;
  border-radius:4px!important;
  background:#fff!important;
  color:var(--muted)!important;
  font-size:7px!important;
  font-weight:700!important;
  text-transform:uppercase!important;
}
.move-history{margin-left:4px!important}
.st-green{color:var(--green)!important}
.st-amber{color:var(--amber)!important}
.st-blue{color:var(--blue)!important}

.agenda-actions{display:flex;gap:4px;flex:0 0 auto;flex-wrap:wrap;justify-content:flex-end}
.btn-wa-line,.btn-move-line,.btn-edit-line,.btn-delete-line{
  height:28px;
  padding:0 7px;
  display:flex;
  align-items:center;
  gap:4px;
  border:1px solid var(--line);
  border-radius:6px;
  background:#fff;
  color:var(--muted);
  font-size:8px;
  font-weight:700;
}
.btn-wa-line:hover{color:var(--green);border-color:#cfe5d5;background:#fbfefc}
.btn-move-line:hover,.btn-edit-line:hover{color:var(--accent);background:var(--soft-bg)}
.btn-delete-line:hover{color:var(--red);border-color:#ead8d5;background:var(--red-soft)}
.btn-wa-line svg,.btn-move-line svg,.btn-edit-line svg,.btn-delete-line svg{width:11px;height:11px}

.lmx-modal-backdrop{
  position:fixed;
  inset:0;
  z-index:200;
  display:grid;
  place-items:center;
  padding:16px;
  background:rgba(23,32,32,.28);
}
.lmx-modal-card{
  width:min(470px,100%);
  max-height:90svh;
  overflow:auto;
  padding:18px;
  border:1px solid var(--line-strong);
  border-radius:8px;
  background:#fff;
}
.modal-wide{width:min(610px,100%)}
.modal-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:8px}
.modal-head h3{margin:0;font-size:16px;font-weight:750;letter-spacing:-.02em}
.modal-head button{
  width:28px;height:28px;display:grid;place-items:center;
  border:1px solid var(--line);border-radius:6px;background:#fff;color:var(--muted);
}
.modal-head button:hover{background:var(--soft-bg)}
.modal-head button svg{width:13px;height:13px}
.modal-copy{margin:0 0 13px;color:var(--muted);font-size:9.5px;line-height:1.5}
.modal-copy strong{color:var(--text)}
.modal-check{margin-bottom:12px}
.modal-actions{display:flex;justify-content:flex-end;gap:7px;margin-top:15px}
.btn-light,.btn-primary,.btn-danger{
  height:32px;
  padding:0 10px;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:5px;
  border-radius:6px;
  font-size:9px;
  font-weight:700;
}
.btn-light{border:1px solid var(--line);background:#fff;color:var(--muted)}
.btn-primary{border:1px solid var(--accent);background:var(--accent);color:#fff}
.btn-danger{border:1px solid var(--red);background:var(--red);color:#fff}
.btn-whatsapp{border-color:var(--green);background:var(--green)}
.btn-primary svg{width:12px;height:12px}

.lmx-panel-actions{display:flex;align-items:center;justify-content:flex-end;gap:7px;flex-wrap:wrap}
.lmx-channel-control{height:31px;display:flex;align-items:center;gap:5px;border:1px solid var(--line);background:#fff;padding:0 4px 0 8px;min-width:220px}
.lmx-channel-control .channel-dot{width:6px;height:6px;border-radius:50%;background:#b7bfbd;flex:0 0 auto}
.lmx-channel-control.is-connected .channel-dot{background:var(--green)}
.lmx-channel-control select{min-width:0;max-width:270px;flex:1;height:29px;border:0;outline:0;background:transparent;color:var(--text-2);font-size:8.5px;font-weight:650;padding:0 3px;cursor:pointer}
.lmx-channel-control select:disabled{cursor:default;color:var(--muted)}
.lmx-channel-control button{width:27px;height:27px;display:grid;place-items:center;border:0;background:transparent;color:var(--muted)}
.lmx-channel-control button:hover{color:var(--accent);background:var(--accent-soft)}
.lmx-channel-control button:disabled{opacity:.45;cursor:default}
.lmx-channel-control button svg{width:12px;height:12px}
.lmx-integration-warning{margin:-5px 0 12px;padding:7px 9px;border:1px solid #ead8d5;background:var(--danger-soft);color:var(--danger);font-size:8px;font-weight:600;line-height:1.4}
.channel-error{display:block;margin:-3px 0 7px;color:var(--danger);font-size:8px;font-weight:600}
.btn-primary:disabled,.btn-whatsapp:disabled{opacity:.5;cursor:not-allowed}

@media (max-width:900px){
  .hide-mobile,.desktop-only{display:none!important}
  .show-mobile{display:flex!important}
  .agendamentos-wrapper{height:auto;min-height:100svh;overflow:visible}
  .gm-app-topbar{position:sticky;top:0;height:58px;flex-basis:58px;padding-top:env(safe-area-inset-top)}
  .gm-topbar-inner{width:100%;padding:0 12px;gap:8px}
  .gm-top-left{gap:6px;flex:1}
  .lmx-home-button{width:31px;height:31px;padding:0;justify-content:center}
  .gm-logo-wrapper{width:27px;height:27px}
  .gm-brand-text{font-size:14px}
  .gm-avatar{width:31px;height:31px}
  .gm-btn-logout{width:31px;height:31px;padding:0;justify-content:center}
  .lmx-notice{top:66px}
  .lmx-mobile-tabs{
    position:sticky;
    top:58px;
    z-index:45;
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:0;
    padding:0 12px;
    background:#fff;
    border-bottom:1px solid var(--line);
  }
  .lmx-mobile-tabs button{
    height:42px;
    display:flex;align-items:center;justify-content:center;gap:6px;
    border:0;border-bottom:2px solid transparent;background:#fff;color:var(--muted);
    font-size:9px;font-weight:700;
  }
  .lmx-mobile-tabs button svg{width:13px;height:13px}
  .lmx-mobile-tabs button.active{border-bottom-color:var(--accent);color:var(--accent)}
  .lmx-split-layout{display:block;overflow:visible}
  .lmx-form-panel,.lmx-calendar-panel{display:none}
  .mobile-novo .lmx-form-panel,.mobile-agenda .lmx-calendar-panel{display:block}
  .lmx-form-panel{min-width:0;border:0}
  .lmx-form-inner{max-width:100%;min-height:auto;display:block;padding:18px 12px 28px}
  .lmx-hero-title{font-size:20px}
  .lmx-hero-sub{font-size:10px}
  .lmx-form-row{grid-template-columns:1fr}
  .lmx-calendar-panel{padding:18px 12px 28px;overflow:visible}
  .lmx-calendar-inner{height:auto;max-width:100%;overflow:visible;padding:0}
  .lmx-panel-head{display:none}
  .agenda-mobile-tools{display:grid;gap:7px;margin-bottom:10px}
  .agenda-mobile-tools .template-button{width:100%}
  .mobile-channel{width:100%;min-width:0;height:34px}
  .mobile-channel select{max-width:none;font-size:8px}
  .lmx-move-banner{position:sticky;top:105px;z-index:30}
  .lmx-calendar-widget{padding-top:12px}
  .cal-grid{gap:0}
  .cal-cell{min-height:43px}
  .agenda-item{grid-template-columns:1fr;gap:0}
  .agenda-time{justify-content:flex-start;padding:8px 4px 0}
  .agenda-card{padding:8px 4px 10px;cursor:default}
  .drag-indicator{display:none}
  .agenda-info-wrap{display:grid;gap:8px}
  .agenda-actions{width:100%;display:grid;grid-template-columns:1fr 1fr}
  .btn-wa-line,.btn-move-line,.btn-edit-line,.btn-delete-line{justify-content:center;height:30px}
  .modal-actions{display:grid;grid-template-columns:1fr}
  .btn-light,.btn-primary,.btn-danger{width:100%;height:34px}
}

@media (max-width:430px){
  .gm-brand-text{font-size:13px}
  .gm-avatar{display:none}
  .cal-header{align-items:flex-start}
  .cal-nav{flex-wrap:wrap;justify-content:flex-end}
  .cal-cell{min-height:39px}
  .lmx-stats-row span{font-size:7px}
  .agenda-actions{grid-template-columns:1fr}
  .lmx-modal-backdrop{padding:9px}
  .lmx-modal-card{padding:15px}
}

@media (prefers-reduced-motion:reduce){
  *,*::before,*::after{scroll-behavior:auto!important;transition:none!important}
}
`;
